import { useState, useEffect } from 'react';
import { useAudioRecorderConfig, useAudioRecorderState } from '../context/AudioRecorderContext';
import { useAndroidAudioRecorderConfig, useAndroidAudioRecorderState } from '../context/AndroidAudioRecorderProvider';
import { useAudioTelegramRecorder } from '../context/AudioTelegramRecorderProvider';
import { handleStopRecording } from '../handlers/generalHandlers';
import { handleError } from '../context/errors/errorUtils';
import { Capacitor } from '@capacitor/core';

export const useAudioRecorderGeneral = ({
  userId,
  stepId,
  contextId,
  source,
  dispatch,
  step,
  state,
  buttonRef,
  attemptCount = 0,
  isTelegramWebApp,
}) => {
  const isAndroidApp = Capacitor.isNativePlatform(); 	
  const [isProcessing, setIsProcessing] = useState(false);

  let permissionGranted = false;
  let isSupported = false;
  let isRecording = false;
  let startRecording = () => {};
  let stopRecording = () => {};
  let telegramWarning = false;

  try {
    if (isTelegramWebApp) {
      const telegramRecorder = useAudioTelegramRecorder();
      if (!telegramRecorder) throw new Error('AUDIO_RECORDER_TELEGRAM_CONTEXT_MISSING');
      ({ isRecording, startRecording, stopRecording, isSupported, permissionGranted, telegramWarning } = telegramRecorder);
    } else if (isAndroidApp) {
      const config = useAndroidAudioRecorderConfig();
      const recorderState = useAndroidAudioRecorderState();
      if (!config || !recorderState) throw new Error('ANDROID_AUDIO_RECORDER_CONTEXT_MISSING');
      ({ isSupported } = config);
      ({ isRecording, startRecording, stopRecording } = recorderState);
      permissionGranted = true;
    } else {
      const config = useAudioRecorderConfig();
      const recorderState = useAudioRecorderState();
      if (!config || !recorderState) throw new Error('AUDIO_RECORDER_CONTEXT_MISSING');
      ({ permissionGranted, isSupported } = config);
      ({ isRecording, startRecording, stopRecording } = recorderState);
    }
  } catch (error) {
    handleError({
      error,
      code: error.message,
      context: { module: 'AudioRecorderGeneral', userId, stepId, contextId },
      showToUser: true,
      dispatch,
    });
    return {
      isRecording: false,
      permissionGranted: false,
      isSupported: false,
      telegramWarning: false,
      isProcessing: false,
      handleStartRecording: () => false,
      handleStopRecording: () => {},
      handleStop: () => {},
    };
  }

  const handleStartRecording = (options = []) => {
    if (isProcessing || !stepId) {
      const blockReasons = [];
      if (isProcessing) blockReasons.push('processing');
      if (!stepId) blockReasons.push('no_step');
      handleError({
        code: 'START_RECORD_BLOCKED',
        error: new Error('Start recording blocked: ' + blockReasons.join(', ')),
        context: { module: 'AudioRecorderGeneral', userId, stepId, contextId, reasons: blockReasons },
        showToUser: false,
      });
      return false;
    }
    dispatch({ type: 'SET_ERROR', payload: '' });
    dispatch({ type: 'SET_RECOGNIZED_TEXT', payload: '' });
    dispatch({ type: 'SET_AUDIO_BLOB', payload: null });
    setIsProcessing(false);

    try {
      startRecording(
        null,
        (blob, data) => handleStop(blob, data),
        options
      );
      return true;
    } catch (error) {
      handleError({
        error,
        code: 'START_RECORD_ERROR',
        context: { userId, stepId, contextId, module: 'AudioRecorderGeneral' },
        dispatch,
        showToUser: true,
      });
      return false;
    }
  };

  const handleStop = async (blob, data) => {
    if (!stepId || !step || !state || !dispatch) {
      handleError({
        code: 'STOP_RECORD_INCOMPLETE_DEPENDENCIES',
        error: new Error('Missing dependencies in handleStop'),
        context: {
          hasStep: !!step,
          hasState: !!state,
          hasDispatch: !!dispatch,
          contextId,
          userId,
          stepId,
          module: 'AudioRecorderGeneral',
        },
        dispatch,
        showToUser: true,
      });
      setIsProcessing(false);
      return;
    }

    setIsProcessing(true);

    try {
      const dependencies = {
        dispatch,
        step,
        state,
        contextId,
        source,
        userId,
        onComplete: () => setIsProcessing(false),
      };
      await handleStopRecording(blob, data, dependencies);
    } catch (error) {
      handleError({
        error,
        code: 'STOP_RECORD_ERROR',
        context: { userId, stepId, contextId, module: 'AudioRecorderGeneral' },
        dispatch,
        showToUser: true,
      });
      setIsProcessing(false);
    }
  };

  // Event handlers are now handled directly in JSX components

  return {
    isRecording,
    permissionGranted,
    isSupported,
    telegramWarning,
    isProcessing,
    handleStartRecording,
    handleStopRecording: () => {
      if (isRecording) {
        stopRecording();
      }
    },
    handleStop,
  };
};
